import { Button, Grid, LoadingOverlay, ScrollArea, Stack } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useEffect } from 'react';

import { Customer } from '@e-origin/shared';

import { LeavePageNotification, MultiLangCodes, StickyContainer, TextInput } from '@components';
import { REPRESENTATIVE_STATUS_FIXED } from '@constants';
import { EditCustomerType } from '@enums';
import { useDeclarant } from '@hooks/declarant';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { handleRepresentativeStatusChange } from './handleRepresentativeStatusChange';
import { useCustomerViewRepresentativeFormInitializer } from './useCustomerViewRepresentativeFormInitializer';

interface CustomerViewRepresentativeProps {
  handleSubmitCustomer(type: EditCustomerType, childForm: UseFormReturnType<any>): void;
  customerDetails: Customer;
  isCreate: boolean;
  isButtonDisabled: boolean;
  isBE: boolean;
}

export const CustomerViewRepresentative: React.FC<CustomerViewRepresentativeProps> = ({
  handleSubmitCustomer,
  customerDetails,
  isButtonDisabled,
  isCreate,
  isBE,
}) => {
  const { form, buildFormValues } = useCustomerViewRepresentativeFormInitializer({ customerDetails });
  const { representative } = form.values;
  const { data: declarant, isLoading } = useDeclarant();
  const codelistSystems = useCodeListSystem({});

  const disableFieldsForNlWithFixedRep = !isBE && representative.status === REPRESENTATIVE_STATUS_FIXED;

  useEffect(() => {
    if (customerDetails) {
      form.setValues(buildFormValues(customerDetails));
      form.resetDirty({} as any);
    }
  }, [customerDetails]);

  const handleStatusChange = (status: string) => {
    form.setFieldValue('representative.status', Number(status));
    handleRepresentativeStatusChange({
      status,
      form,
      customerDetails,
      buildFormValues,
    });
  };

  if (!declarant) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <>
      <LeavePageNotification hasUnsavedChanges={form.isDirty()} />

      <ScrollArea h="100%" offsetScrollbars="y">
        <Stack mb="xl" mx="xl">
          <Grid>
            <Grid.Col span={2.5}>
              <TextInput {...form.getInputProps('representative.name')} label="Name" disabled={disableFieldsForNlWithFixedRep} />
            </Grid.Col>
            <Grid.Col span={3.5}>{isBE && <TextInput {...form.getInputProps('representative.identificationNumber')} label="Identification number" />}</Grid.Col>
          </Grid>
          <Grid>
            <Grid.Col span={3.5}>
              <TextInput
                {...form.getInputProps('representative.identificationNumberIDMS')}
                label={`Identification number ${isBE ? 'IDMS' : 'DMS'}`}
                disabled={disableFieldsForNlWithFixedRep}
              />
            </Grid.Col>
            <Grid.Col span={2.5}>
              <MultiLangCodes
                value={String(representative.status || '')}
                label="Status"
                code="CL094"
                system={codelistSystems}
                onChange={(value) => handleStatusChange(value)}
              />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={4}>
              <TextInput {...form.getInputProps('representative.address.street')} label="Address" disabled={disableFieldsForNlWithFixedRep} />
            </Grid.Col>
            <Grid.Col span={2}>
              <TextInput {...form.getInputProps('representative.address.postcode')} label="Postcode" disabled={disableFieldsForNlWithFixedRep} />
            </Grid.Col>
            <Grid.Col span={2}>
              <TextInput {...form.getInputProps('representative.address.city')} label="City" disabled={disableFieldsForNlWithFixedRep} />
            </Grid.Col>
            <Grid.Col span={2}>
              <TextInput {...form.getInputProps('representative.address.country')} label="Country" disabled={disableFieldsForNlWithFixedRep} />
            </Grid.Col>
          </Grid>
        </Stack>

        <StickyContainer>
          <Button
            disabled={isButtonDisabled || !form.isValid()}
            onClick={() => handleSubmitCustomer(EditCustomerType.REPRESENTATIVE, form)}
            type="submit"
            variant="filled"
            size="lg"
          >
            {isCreate ? 'Create' : 'Save'}
          </Button>
        </StickyContainer>
      </ScrollArea>
    </>
  );
};
